/**
 * Mad Muscle — motion runtime.
 *
 * Three jobs, in this order of importance:
 *   1. Scroll reveals via ONE shared IntersectionObserver (never one per element).
 *   2. Lenis smooth scroll, only when the user has not asked for reduced motion.
 *   3. Header scroll state (transparent -> solid), driven by a sentinel observer
 *      rather than a scroll listener.
 *
 * No animation library beyond Lenis. The de-stacking claim cards are CSS
 * `position: sticky` rather than a scrubbed GSAP timeline — which is both
 * cheaper and more robust, and matches what the reference sites actually do:
 * Drink Still ships the best page in the set on Lenis alone.
 *
 * Spec: Obsidian > Projects/Mad Muscle/Website/Design System.md §4
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ── 1. reveals ────────────────────────────────────────────────────────── */

  /**
   * Observers must use the same root as the real scroller (see `scroller()`
   * below) or they fire for everything at once on first paint.
   */
  const observerRoot = () => {
    const el = document.querySelector('.page-wrapper');
    if (!el) return null;
    const overflowY = getComputedStyle(el).overflowY;
    return overflowY === 'auto' || overflowY === 'scroll' ? el : null;
  };

  let revealObserver = null;

  const getRevealObserver = () => {
    revealObserver ||= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
        }
      },
      { root: observerRoot(), rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
    );
    return revealObserver;
  };

  const observeReveals = (root = document) => {
    const observer = getRevealObserver();
    for (const el of root.querySelectorAll('.mm-reveal:not(.is-in)')) {
      observer.observe(el);
    }
  };

  /**
   * Stagger children of a [data-mm-stagger] group by setting --mm-i, which the
   * .mm-reveal transition-delay calc() reads. Cheaper than per-element JS delays
   * and it survives a CSS-only fallback.
   */
  const applyStagger = (root = document) => {
    for (const group of root.querySelectorAll('[data-mm-stagger]')) {
      const items = group.querySelectorAll(':scope > .mm-reveal');
      items.forEach((item, i) => item.style.setProperty('--mm-i', String(i)));
    }
  };

  /* ── 2. smooth scroll ──────────────────────────────────────────────────── */

  let lenis = null;

  /**
   * Horizon does NOT scroll the window. base.css gives `.page-wrapper`
   * `height: 100dvh; overflow-y: auto`, so that element is the scroll container.
   * Pointing Lenis at `window` makes it set `overflow: hidden` on <html> and the
   * whole document collapses to one viewport. Always hand it the real scroller.
   */
  const scroller = () => document.querySelector('.page-wrapper');

  const isElementScroller = () => {
    const el = scroller();
    if (!el) return false;
    const overflowY = getComputedStyle(el).overflowY;
    return overflowY === 'auto' || overflowY === 'scroll';
  };

  const startLenis = () => {
    if (lenis || reduced.matches || typeof window.Lenis === 'undefined') return;
    const wrapper = isElementScroller() ? scroller() : window;
    lenis = new window.Lenis({
      wrapper,
      content: wrapper === window ? document.documentElement : wrapper.firstElementChild || wrapper,
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // expo-out
      smoothWheel: true,
      touchMultiplier: 1.6,
    });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
  };

  const stopLenis = () => {
    if (!lenis) return;
    lenis.destroy();
    lenis = null;
  };

  reduced.addEventListener('change', () => (reduced.matches ? stopLenis() : startLenis()));

  /* ── 3. in-page anchors ────────────────────────────────────────────────── */

  /**
   * Anchor links must be routed through Lenis. `scrollIntoView` and native
   * `:target` jumps both drive the scroll container directly, which Lenis is
   * simultaneously animating — they fight, and the page lands somewhere between
   * the two. Handing the target to Lenis keeps one thing in charge of scroll.
   */
  const initAnchors = () => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-mm-scrollto]');
      if (!link) return;

      const id = link.getAttribute('href')?.slice(1);
      const target = id && document.getElementById(id);
      if (!target) return; // let the browser handle a dead anchor

      event.preventDefault();
      const bar = document.querySelector('#header-group .header-section .header');
      const offset = -((bar?.getBoundingClientRect().height ?? 0) + 16);

      if (lenis) {
        lenis.scrollTo(target, { offset });
      } else {
        const box = target.getBoundingClientRect();
        const host = isElementScroller() ? scroller() : window;
        const top = box.top + (isElementScroller() ? host.scrollTop : window.scrollY) + offset;
        host.scrollTo({ top, behavior: reduced.matches ? 'auto' : 'smooth' });
      }
    });
  };

  /* ── 4. header height ──────────────────────────────────────────────────── */

  /**
   * Publish the height of the bar that actually stays on screen as
   * `--mm-header-h`.
   *
   * Horizon's own `--header-group-height` is not that number: it measures the
   * whole header group, which on this store is 33px of announcement bar, while
   * the part that sticks is the 66px header inside a zero-height sticky rail.
   * Anything that offsets itself below the sticky header — the pinned claim
   * cards, the sticky product stage, anchor scrolling — needs the real value or
   * it lands underneath the nav.
   */
  const publishHeaderHeight = () => {
    const bar = document.querySelector('#header-group .header-section .header');
    const h = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    if (h > 0) document.documentElement.style.setProperty('--mm-header-h', `${h}px`);
  };

  /* ── 5. header scroll state ────────────────────────────────────────────── */

  const initHeaderState = () => {
    const sentinel = document.getElementById('mm-scroll-sentinel');
    if (!sentinel) return;
    new IntersectionObserver(
      ([entry]) => document.documentElement.classList.toggle('mm-scrolled', !entry.isIntersecting),
      { root: observerRoot(), threshold: 0 }
    ).observe(sentinel);
  };

  /* ── boot ──────────────────────────────────────────────────────────────── */

  const boot = () => {
    applyStagger();
    observeReveals();
    publishHeaderHeight();
    initHeaderState();
    initAnchors();
    startLenis();
    window.addEventListener('resize', publishHeaderHeight, { passive: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', (event) => {
    applyStagger(event.target);
    observeReveals(event.target);
  });
})();
